import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, ArrowLeft, Facebook, Twitter, Link as LinkIcon, Share2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { BIZ } from "../lib/constants";

export default function BlogPost() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => { api.blogPost(slug).then(setData).catch(() => setErr(true)); }, [slug]);

    if (err) return <div className="ls-container py-32 text-center text-white">Post not found. <Link to="/blog" className="link-underline" style={{ color: "var(--ls-gold)" }}>Read more</Link></div>;
    if (!data) return <div className="ls-container py-32 text-center" style={{ color: "var(--ls-text-muted)" }}>Loading…</div>;

    const { post, related } = data;
    const url = `${BIZ.siteUrl}/blog/${post.slug}`;
    const shareEnc = encodeURIComponent(url);
    const titleEnc = encodeURIComponent(post.title);

    const jsonLd = {
        "@context": "https://schema.org", "@type": "Article",
        headline: post.title, description: post.excerpt, image: post.cover_image_url,
        datePublished: post.published_at, dateModified: post.updated_at,
        author: { "@type": "Organization", name: post.author || "Liquor Studio" },
        publisher: { "@type": "Organization", name: BIZ.name, logo: { "@type": "ImageObject", url: `${BIZ.siteUrl}/og-image.png` } },
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
    };

    return (
        <>
            <SEO title={post.seo_title || post.title} description={post.seo_description || post.excerpt} path={`/blog/${post.slug}`} image={post.cover_image_url} type="article" jsonLd={jsonLd} />

            <article style={{ paddingTop: 120 }}>
                <div className="ls-container" style={{ maxWidth: 820 }}>
                    <Link to="/blog" className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--ls-text-secondary)" }} data-testid="back-to-blog">
                        <ArrowLeft size={14} /> All articles
                    </Link>

                    <div className="mt-8">
                        <span className="eyebrow">{post.category}</span>
                        <h1 className="font-display text-white mt-4" style={{ fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 500, lineHeight: 1.05, letterSpacing: "-0.02em" }} data-testid="blog-title">
                            {post.title}
                        </h1>
                        <div className="mt-5 flex items-center gap-5 text-sm" style={{ color: "var(--ls-text-muted)" }}>
                            <span>{post.author}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1"><Clock size={13} /> {post.reading_minutes} min read</span>
                            <span>·</span>
                            <span>{new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                        </div>
                    </div>

                    <img src={post.cover_image_url} alt={post.title} style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", marginTop: 40, borderRadius: 6, border: "1px solid var(--ls-border-strong)" }} />

                    {/* Share */}
                    <div className="flex items-center gap-2 mt-8" data-testid="blog-share">
                        <span className="text-xs uppercase tracking-wider mr-2" style={{ color: "var(--ls-text-muted)" }}>Share</span>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareEnc}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="btn-ghost" style={{ padding: 8 }}><Facebook size={14} /></a>
                        <a href={`https://twitter.com/intent/tweet?url=${shareEnc}&text=${titleEnc}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="btn-ghost" style={{ padding: 8 }}><Twitter size={14} /></a>
                        <a href={`https://pinterest.com/pin/create/button/?url=${shareEnc}&description=${titleEnc}&media=${encodeURIComponent(post.cover_image_url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Pinterest" className="btn-ghost" style={{ padding: 8 }}><Share2 size={14} /></a>
                        <button onClick={copyLink} aria-label="Copy link" className="btn-ghost" style={{ padding: 8 }} data-testid="blog-copy-link">
                            <LinkIcon size={14} />
                        </button>
                        {copied && <span className="text-xs ml-2" style={{ color: "var(--ls-gold)" }}>Copied!</span>}
                    </div>

                    {/* Markdown body */}
                    <div className="mt-12 prose-ls" data-testid="blog-content" style={{ color: "var(--ls-text-secondary)", fontSize: 18, lineHeight: 1.8 }}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                h2: ({ node, ...p }) => <h2 className="font-display text-white mt-12 mb-4" style={{ fontSize: 30, fontWeight: 500, lineHeight: 1.15 }} {...p} />,
                                h3: ({ node, ...p }) => <h3 className="font-display text-white mt-8 mb-3" style={{ fontSize: 22, fontWeight: 500 }} {...p} />,
                                p: ({ node, ...p }) => <p className="mb-5" {...p} />,
                                ul: ({ node, ...p }) => <ul className="mb-5 space-y-2 list-disc pl-6" {...p} />,
                                ol: ({ node, ...p }) => <ol className="mb-5 space-y-2 list-decimal pl-6" {...p} />,
                                a: ({ node, ...p }) => <a className="link-underline" style={{ color: "var(--ls-gold)" }} {...p} />,
                                blockquote: ({ node, ...p }) => <blockquote className="border-l-2 pl-5 my-6 italic" style={{ borderColor: "var(--ls-gold)", color: "var(--ls-text)" }} {...p} />,
                                strong: ({ node, ...p }) => <strong className="text-white" {...p} />,
                            }}
                        >{post.content}</ReactMarkdown>
                    </div>

                    {related?.length > 0 && (
                        <div className="mt-24 pt-12" style={{ borderTop: "1px solid var(--ls-border)" }}>
                            <h2 className="font-display text-white mb-8" style={{ fontSize: 28, fontWeight: 500 }}>Keep reading</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="blog-related">
                                {related.map(r => (
                                    <Link key={r.slug} to={`/blog/${r.slug}`} className="cat-card block" data-testid={`blog-related-${r.slug}`}>
                                        <div style={{ aspectRatio: "16/10", overflow: "hidden" }}>
                                            <img src={r.cover_image_url} alt={r.title} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        </div>
                                        <div className="p-5">
                                            <span className="pill" style={{ fontSize: 9, padding: "3px 8px" }}>{r.category}</span>
                                            <h3 className="font-display text-white mt-3" style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.25 }}>{r.title}</h3>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </article>
        </>
    );
}
